import makeStyles from '@mui/styles/makeStyles';
import { FC, useState } from 'react';
import { RaptorTheme, greys } from '../../../../../styling/theme';
import {
  addCommasToNumbersAndRound,
  percentageToTwoDecimalPlacesNoHundred,
  roundNumberToTwoDecimalPlaces,
} from '../../../../../utilities/numberFormatters';
import GridItem from '../../../../layout/GridComponents/GridItem';
import ESGChart from '../../esgExposure/esgExposureComponents/ESGChart';
import GeneralSelect from '../../../../selects/GeneralSelect';
import HistoricalScoresLineChart from './HistoricalScoresLineChart.component';
import { Typography } from '@mui/material';
import { IESGPortfolioSummary } from '../types/esg-portfolio.types';
import { useFundEsgTimeSeriesData } from '../../../../../api/rs-hg/esg';
import { QueryBoundary } from '../../../../utilityComponents/QueryBoundary';

interface ESGOverviewPanelProps {
  data: IESGPortfolioSummary;
  fundId: string | null;
  fundName: string | null;
  positionDate?: string;
}

const useOverviewPanelStyles = makeStyles<RaptorTheme>((theme) => ({
  componentsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: 8,
  },
  chartContainer: {
    width: '60%',
  },
  scoreContainer: {
    width: '20%',
    justifyContent: 'center',
  },
  basicInfoContainer: {
    width: '20%',
    justifyContent: 'center',
  },
  fundInfoLine: {
    display: 'flex',
  },
  individualChart: {
    marginBottom: '0.62rem',
    height: '12.50rem',
    width: '12.50rem',
    justifyContent: 'center',
  },
  generalSelectContainer: {
    position: 'relative',
    // height: 500,
    width: '80%',
  },
  pickerRoot: {
    backgroundColor: 'white',
    borderRadius: 8,
    boxShadow: theme.shadows[5],
    marginRight: '0.75rem',
    height: '3.12rem',
    width: '100%',
    marginBottom: '0.75rem',
  },
  selectRoot: {
    width: '100%',
  },
}));

function totalProxified(data: any): string {
  if (!data.length) return `${0}/${data.length}`;

  let count = 0;
  data.forEach((pos: any) => {
    if (pos.uses_proxy === 'yes') {
      count++;
    }
  });

  return `${count}/${data.length}`;
}

// IESGPortfolioSummary

export function esgVar(data: IESGPortfolioSummary): number | null {
  const metrics = data.esg_risk_metrics;

  if (!metrics || metrics === 'disabled_module') return null;
  if (
    !metrics.implied_historical_ts_data ||
    Object.keys(metrics.implied_historical_ts_data).length === 0
  )
    return null;

  return metrics.implied_historical_ts_data.esg_data?.var ?? null;
}

export function esgVol(data: IESGPortfolioSummary): number | null {
  const metrics = data.esg_risk_metrics;

  if (!metrics || metrics === 'disabled_module') return null;
  if (
    !metrics.implied_historical_ts_data ||
    Object.keys(metrics.implied_historical_ts_data).length === 0
  )
    return null;

  return metrics.implied_historical_ts_data.esg_data?.std ?? null;
}

export type ESGScoreKey = 'ESG_Score' | 'E_Score' | 'S_Score' | 'G_Score';

// FUnction for getting the fund esg score from the data
function getEsgScore(data: IESGPortfolioSummary): number {
  if (!data) return 0;
  if ('no data' in data) return 0;
  // Check the version number before returning
  const esgData = data;
  if (esgData.version === 2) return esgData['portfolio_scores']['gross']['esg'];
  else if (esgData.version === 3)
    return esgData['portfolio_scores']['ESG_Score'];
  else return esgData['portfolio_scores']['gross']['esg'];
}

const ESGPortfolioOverviewPanel: FC<ESGOverviewPanelProps> = ({
  data,
  fundId,
  fundName,
  positionDate,
}) => {
  const classes = useOverviewPanelStyles();

  const [scoreType, setScoreType] = useState<ESGScoreKey>('ESG_Score');

  const esgScore = getEsgScore(data);

  const fundEsgTimeSeriesQuery = useFundEsgTimeSeriesData(
    fundId ?? undefined,
    positionDate,
  );

  return (
    <GridItem xs={12} card>
      <div className={classes.componentsContainer}>
        <div className={classes.basicInfoContainer}>
          <Typography variant="h2" align="left">
            Fund Information
          </Typography>
          <div className={classes.fundInfoLine}>
            <Typography variant="h3">Fund Name: </Typography>
            <Typography
              variant="h3"
              style={{
                color: greys.grey600,
                marginLeft: '0.62rem',
                marginBottom: '0.31rem',
              }}
            >
              {fundName}
            </Typography>
          </div>
          <div className={classes.fundInfoLine}>
            <Typography variant="h3">NAV: </Typography>
            <Typography
              variant="h3"
              style={{
                color: greys.grey600,
                marginLeft: '0.62rem',
                marginBottom: '0.31rem',
              }}
            >
              {addCommasToNumbersAndRound(data.nav)}
            </Typography>
          </div>
          <div className={classes.fundInfoLine}>
            <Typography variant="h3">Total Proxified Positions: </Typography>
            <Typography
              variant="h3"
              style={{
                color: greys.grey600,
                marginLeft: '0.62rem',
                marginBottom: '0.31rem',
              }}
            >
              {totalProxified(data.positions)}
            </Typography>
          </div>
          {data.esg_subscription_package !== 'esg_premium_package' ? null : (
            <>
              <Typography
                variant="h2"
                align="left"
                style={{ marginTop: '1.25rem' }}
              >
                Historical ESG Volatility
              </Typography>
              <div className={classes.fundInfoLine}>
                <Typography variant="h3">ESG Volatility: </Typography>
                <Typography
                  variant="h3"
                  style={{
                    color: greys.grey600,
                    marginLeft: '0.62rem',
                    marginBottom: '0.31rem',
                  }}
                >
                  {esgVol(data)
                    ? percentageToTwoDecimalPlacesNoHundred(esgVol(data))
                    : '-'}
                </Typography>
              </div>
              <div className={classes.fundInfoLine}>
                <Typography variant="h3">ESG VaR: </Typography>
                <Typography
                  variant="h3"
                  style={{
                    color: greys.grey600,
                    marginLeft: '0.62rem',
                    marginBottom: '0.31rem',
                  }}
                >
                  {esgVar(data)
                    ? percentageToTwoDecimalPlacesNoHundred(esgVar(data))
                    : '-'}
                </Typography>
              </div>
            </>
          )}
          <div
            className={classes.generalSelectContainer}
            style={{
              marginTop: '1.25rem',
            }}
          >
            <GeneralSelect
              labels={[
                'ESG',
                'Environmental',
                'Social',
                'Corporate Governance',
              ]}
              values={['ESG_Score', 'E_Score', 'S_Score', 'G_Score']}
              selected={scoreType}
              setSelected={setScoreType}
              placeholderValue=""
              placeholderColor={greys.grey900}
              selectTitle={'Chart Score Type'}
              customStyles={{
                pickerRoot: classes.pickerRoot,
                selectRoot: classes.selectRoot,
              }}
            />
          </div>
        </div>

        <div className={classes.chartContainer}>
          <QueryBoundary
            isLoading={fundEsgTimeSeriesQuery.isLoading}
            isError={fundEsgTimeSeriesQuery.isError}
            data={fundEsgTimeSeriesQuery.data}
            loadingMessages={['Loading Historical Data...']}
            errorMessage="No Historical Data Found"
            noDataMessage="No Historical Data Found"
            card
          >
            {(timeSeriesData) => (
              <HistoricalScoresLineChart
                data={timeSeriesData}
                scoreType={scoreType}
              />
            )}
          </QueryBoundary>
        </div>

        <div className={classes.scoreContainer}>
          <div
            style={{
              height: '15.62rem',
              width: '15.62rem',
              display: 'flex-column',
              marginLeft: '2.50rem',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="h1"
              align="center"
              style={{ marginBottom: '1.25rem', fontWeight: 100 }}
            >
              ESG Score
            </Typography>
            <ESGChart value={esgScore} />
            <Typography
              variant="h2"
              align="center"
              style={{ marginTop: '1.25rem' }}
            >
              {roundNumberToTwoDecimalPlaces(esgScore)}
            </Typography>
            <Typography variant="h2" align="center" style={{ opacity: 0.3 }}>
              {' '}
              / 100.00
            </Typography>
          </div>
        </div>
      </div>
    </GridItem>
  );
};

export default ESGPortfolioOverviewPanel;
